import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "../index";

// Basic selectors
export const selectPractice = (state: RootState): RootState["practice"] =>
  state.practice;
export const selectCurrentText = (
  state: RootState,
): RootState["practice"]["currentText"] => state.practice.currentText;
export const selectProgress = (
  state: RootState,
): RootState["practice"]["progress"] => state.practice.progress;
export const selectStats = (state: RootState): RootState["practice"]["stats"] =>
  state.practice.stats;
export const selectPracticeLoading = (
  state: RootState,
): RootState["practice"]["loading"] => state.practice.loading;
export const selectPracticeSaving = (
  state: RootState,
): RootState["practice"]["saving"] => state.practice.saving;
export const selectPracticeError = (
  state: RootState,
): RootState["practice"]["error"] => state.practice.error;

// Audio state selectors
export const selectAudioState = (
  state: RootState,
): RootState["practice"]["audio"] => state.practice.audio;
export const selectAudioLoading = (
  state: RootState,
): RootState["practice"]["audio"]["loading"] => state.practice.audio.loading;
export const selectIsPlaying = (
  state: RootState,
): RootState["practice"]["audio"]["isPlaying"] =>
  state.practice.audio.isPlaying;
export const selectAudioCurrentTime = (
  state: RootState,
): RootState["practice"]["audio"]["currentTime"] =>
  state.practice.audio.currentTime;
export const selectAudioDuration = (
  state: RootState,
): RootState["practice"]["audio"]["duration"] => state.practice.audio.duration;
export const selectAudioVolume = (
  state: RootState,
): RootState["practice"]["audio"]["volume"] => state.practice.audio.volume;
export const selectPlaybackSpeed = (
  state: RootState,
): RootState["practice"]["audio"]["playbackSpeed"] =>
  state.practice.audio.playbackSpeed;
export const selectAudioUrl = (
  state: RootState,
): RootState["practice"]["audio"]["audioUrl"] => state.practice.audio.audioUrl;
export const selectAudioCurrentVoiceId = (
  state: RootState,
): RootState["practice"]["audio"]["currentVoiceId"] =>
  state.practice.audio.currentVoiceId;

// Voice settings selectors
export const selectVoiceState = (
  state: RootState,
): RootState["practice"]["voice"] => state.practice.voice;
export const selectSelectedVoice = (
  state: RootState,
): RootState["practice"]["voice"]["selectedVoice"] =>
  state.practice.voice.selectedVoice;
export const selectAvailableVoices = (
  state: RootState,
): RootState["practice"]["voice"]["availableVoices"] =>
  state.practice.voice.availableVoices;

// UI state selectors
export const selectUIState = (state: RootState): RootState["practice"]["ui"] =>
  state.practice.ui;
export const selectShowSettings = (
  state: RootState,
): RootState["practice"]["ui"]["showSettings"] =>
  state.practice.ui.showSettings;
export const selectShowSubscriptionModal = (
  state: RootState,
): RootState["practice"]["ui"]["showSubscriptionModal"] =>
  state.practice.ui.showSubscriptionModal;
export const selectShowTranslation = (
  state: RootState,
): RootState["practice"]["ui"]["showTranslation"] =>
  state.practice.ui.showTranslation;
export const selectTranslation = (
  state: RootState,
): RootState["practice"]["ui"]["translation"] => state.practice.ui.translation;
export const selectTranslationLoading = (
  state: RootState,
): RootState["practice"]["ui"]["translationLoading"] =>
  state.practice.ui.translationLoading;

// Session state selectors
export const selectSessionState = (
  state: RootState,
): RootState["practice"]["session"] => state.practice.session;
export const selectSessionStartTime = (
  state: RootState,
): RootState["practice"]["session"]["startTime"] =>
  state.practice.session.startTime;
export const selectSessionsCompleted = (
  state: RootState,
): RootState["practice"]["session"]["sessionsCompleted"] =>
  state.practice.session.sessionsCompleted;
export const selectSessionProgress = (
  state: RootState,
): RootState["practice"]["session"]["sessionProgress"] =>
  state.practice.session.sessionProgress;
export const selectIsSessionCompleted = (
  state: RootState,
): RootState["practice"]["session"]["isCompleted"] =>
  state.practice.session.isCompleted;
export const selectSessionTime = (
  state: RootState,
): RootState["practice"]["session"]["sessionTime"] =>
  state.practice.session.sessionTime;

// Rating state selectors
export const selectRatingState = (
  state: RootState,
): RootState["practice"]["rating"] => state.practice.rating;
export const selectUserRating = (
  state: RootState,
): RootState["practice"]["rating"]["userRating"] =>
  state.practice.rating.userRating;
export const selectIsFavorited = (
  state: RootState,
): RootState["practice"]["rating"]["isFavorited"] =>
  state.practice.rating.isFavorited;
export const selectRatingSubmitted = (
  state: RootState,
): RootState["practice"]["rating"]["ratingSubmitted"] =>
  state.practice.rating.ratingSubmitted;
export const selectRatingLoading = (
  state: RootState,
): RootState["practice"]["rating"]["ratingLoading"] =>
  state.practice.rating.ratingLoading;

// Memoized selectors
export const selectProgressForText = createSelector(
  [selectProgress, (_state: RootState, textId: string) => textId],
  (progress, textId): RootState["practice"]["progress"][number] | undefined =>
    progress.find((p) => p.textId === textId),
);

export const selectCompletedTexts = createSelector(
  [selectProgress],
  (progress): RootState["practice"]["progress"] =>
    progress.filter((p) => p.completed),
);

export const selectTotalTimeSpent = createSelector(
  [selectStats],
  (stats): RootState["practice"]["stats"]["totalTimeSpent"] =>
    stats.totalTimeSpent,
);

export const selectTotalTextsCompleted = createSelector(
  [selectStats],
  (stats): RootState["practice"]["stats"]["totalTextsCompleted"] =>
    stats.totalTextsCompleted,
);

export const selectAverageScore = createSelector(
  [selectStats],
  (stats): RootState["practice"]["stats"]["averageScore"] => stats.averageScore,
);

// New computed selectors for extended functionality
export const selectCanRate = createSelector(
  [selectCurrentText, selectRatingSubmitted],
  (text, ratingSubmitted): boolean => Boolean(text && !ratingSubmitted),
);

export const selectSessionDuration = createSelector(
  [selectSessionStartTime],
  (startTime): number => {
    if (!startTime) return 0;
    return Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
  },
);

export const selectCurrentVoice = createSelector(
  [selectSelectedVoice, selectAvailableVoices],
  (
    selectedVoiceId,
    availableVoices,
  ): RootState["practice"]["voice"]["availableVoices"][number] | null => {
    return (
      availableVoices.find((voice) => voice.id === selectedVoiceId) || null
    );
  },
);

export const selectIsAudioReady = createSelector(
  [selectAudioLoading, selectAudioUrl],
  (loading, audioUrl): boolean => !loading && !!audioUrl,
);

export const selectAudioProgress = createSelector(
  [selectAudioCurrentTime, selectAudioDuration],
  (currentTime, duration): number => {
    if (duration === 0) return 0;
    return Math.round((currentTime / duration) * 100);
  },
);
